<template>
  <div class="revenue-page">
    <div class="revenue-bg"></div>

    <header class="revenue-header">
      <div>
        <p class="eyebrow">PLATFORM REVENUE</p>
        <h1>平台收入中心</h1>
        <p>按白皮书标准转账费 1% 统计任务服务手续费，用户侧只展示服务方扣费后预计收入。</p>
      </div>
      <div class="header-actions">
        <button class="ghost-btn" @click="$router.push({ name: 'adminLogin' })">返回控制台入口</button>
        <button class="primary-btn" @click="fetchRevenue">刷新数据</button>
      </div>
    </header>

    <section v-loading="loading" class="metric-grid">
      <article class="metric-card">
        <span>今日平台收入</span>
        <strong>{{ formatCoin(overview.todayPlatformIncome) }}</strong>
        <p>RoboCoin</p>
      </article>
      <article class="metric-card">
        <span>当月平台收入</span>
        <strong>{{ formatCoin(overview.monthPlatformIncome) }}</strong>
        <p>RoboCoin</p>
      </article>
      <article class="metric-card">
        <span>累计平台收入</span>
        <strong>{{ formatCoin(overview.totalPlatformIncome) }}</strong>
        <p>RoboCoin</p>
      </article>
      <article class="metric-card">
        <span>今日交易额</span>
        <strong>{{ formatCoin(overview.todayTradeAmount) }}</strong>
        <p>{{ overview.todayTransactions || 0 }} 笔交易</p>
      </article>
    </section>

    <section class="analytics-grid">
      <article class="panel-card">
        <div class="panel-title">
          <div>
            <p class="eyebrow">7 DAYS</p>
            <h2>收入趋势</h2>
          </div>
          <span>费率 {{ feeRateText }}</span>
        </div>
        <div class="trend-list">
          <div v-for="item in dailyTrend" :key="item.date" class="trend-row">
            <span>{{ item.date.slice(5) }}</span>
            <div class="trend-track">
              <i :style="{ width: trendWidth(item.platformIncome) }"></i>
            </div>
            <strong>{{ formatCoin(item.platformIncome) }}</strong>
          </div>
        </div>
      </article>

      <article class="panel-card">
        <div class="panel-title">
          <div>
            <p class="eyebrow">SERVICE</p>
            <h2>服务类型收入</h2>
          </div>
          <span>{{ overview.totalTransactions || 0 }} 笔</span>
        </div>
        <div class="service-list">
          <div v-for="item in serviceTypeStats" :key="item.serviceType" class="service-row">
            <div>
              <strong>{{ serviceTypeLabel(item.serviceType) }}</strong>
              <span>{{ item.transactions }} 笔 · 交易额 {{ formatCoin(item.tradeAmount) }}</span>
            </div>
            <em>{{ formatCoin(item.platformIncome) }}</em>
          </div>
          <el-empty v-if="serviceTypeStats.length === 0" description="暂无收入数据"></el-empty>
        </div>
      </article>
    </section>

    <section class="report-card">
      <div class="panel-title">
        <div>
          <p class="eyebrow">REPORT</p>
          <h2>手续费流水报表</h2>
        </div>
        <div class="filter-row">
          <el-select v-model="filters.status" clearable placeholder="收入状态" @change="fetchRecords">
            <el-option label="预估" value="ESTIMATED"></el-option>
            <el-option label="已结算" value="SETTLED"></el-option>
            <el-option label="已取消" value="CANCELLED"></el-option>
          </el-select>
          <el-select v-model="filters.serviceType" clearable placeholder="服务类型" @change="fetchRecords">
            <el-option label="上门服务" value="HOME_SERVICE"></el-option>
            <el-option label="接驳服务" value="SHUTTLE"></el-option>
          </el-select>
        </div>
      </div>

      <el-table v-loading="recordsLoading" :data="records" class="revenue-table">
        <el-table-column prop="id" label="流水ID" width="90"></el-table-column>
        <el-table-column prop="orderId" label="订单ID" width="90"></el-table-column>
        <el-table-column label="服务类型" width="110">
          <template #default="{ row }">{{ serviceTypeLabel(row.serviceType) }}</template>
        </el-table-column>
        <el-table-column label="交易总额" min-width="130">
          <template #default="{ row }">{{ formatCoin(row.grossRobocoin) }} RoboCoin</template>
        </el-table-column>
        <el-table-column label="平台收入" min-width="130">
          <template #default="{ row }">{{ formatCoin(row.platformFeeRobocoin) }} RoboCoin</template>
        </el-table-column>
        <el-table-column label="服务方收入" min-width="140">
          <template #default="{ row }">{{ formatCoin(row.providerIncomeRobocoin) }} RoboCoin</template>
        </el-table-column>
        <el-table-column label="状态" width="110">
          <template #default="{ row }">
            <el-tag :type="statusTag(row.revenueStatus)">{{ statusLabel(row.revenueStatus) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="note" label="说明" min-width="180"></el-table-column>
        <el-table-column prop="createTime" label="时间" min-width="170"></el-table-column>
      </el-table>
    </section>
  </div>
</template>

<script>
import { getAdminToken } from '../utils/auth'
import { getRevenueList, getRevenueOverview } from '../api/market'

export default {
  name: 'AdminRevenue',
  data() {
    return {
      loading: false,
      recordsLoading: false,
      overview: {},
      records: [],
      filters: {
        status: '',
        serviceType: ''
      }
    }
  },
  computed: {
    dailyTrend() {
      return this.overview.dailyTrend || []
    },
    serviceTypeStats() {
      return this.overview.serviceTypeStats || []
    },
    feeRateText() {
      return `${(Number(this.overview.feeRate || 0.01) * 100).toFixed(2)}%`
    },
    maxTrendIncome() {
      const values = this.dailyTrend.map(item => Number(item.platformIncome || 0))
      return Math.max(0.01, ...values)
    }
  },
  created() {
    if (!getAdminToken()) {
      this.$router.replace({ name: 'home' })
      return
    }
    this.fetchRevenue()
  },
  methods: {
    async fetchRevenue() {
      this.loading = true
      await Promise.all([this.fetchOverview(), this.fetchRecords()])
      this.loading = false
    },
    async fetchOverview() {
      try {
        const response = await getRevenueOverview()
        if (response.data.code === 200) {
          this.overview = response.data.data || {}
        } else {
          this.$message.error(response.data.msg || '收入概览加载失败')
        }
      } catch (error) {
        this.$message.error('收入概览请求失败')
      }
    },
    async fetchRecords() {
      this.recordsLoading = true
      try {
        const response = await getRevenueList({
          status: this.filters.status || undefined,
          serviceType: this.filters.serviceType || undefined
        })
        if (response.data.code === 200) {
          this.records = response.data.data || []
        } else {
          this.$message.error(response.data.msg || '收入流水加载失败')
        }
      } catch (error) {
        this.$message.error('收入流水请求失败')
      } finally {
        this.recordsLoading = false
      }
    },
    trendWidth(value) {
      const percent = Math.max(4, (Number(value || 0) / this.maxTrendIncome) * 100)
      return `${Math.min(100, percent)}%`
    },
    formatCoin(value) {
      const numberValue = Number(value || 0)
      return Number.isFinite(numberValue) ? numberValue.toFixed(2) : '0.00'
    },
    serviceTypeLabel(value) {
      if (value === 'SHUTTLE') return '接驳服务'
      if (value === 'HOME_SERVICE') return '上门服务'
      return value || '未知服务'
    },
    statusLabel(value) {
      if (value === 'SETTLED') return '已结算'
      if (value === 'CANCELLED') return '已取消'
      return '预估'
    },
    statusTag(value) {
      if (value === 'SETTLED') return 'success'
      if (value === 'CANCELLED') return 'info'
      return 'warning'
    }
  }
}
</script>

<style scoped>
.revenue-page {
  position: relative;
  min-height: 100vh;
  padding: 34px;
  overflow: hidden;
  color: #fff7ec;
  background:
    radial-gradient(circle at 10% 10%, rgba(89, 216, 170, 0.18), transparent 28%),
    radial-gradient(circle at 90% 0%, rgba(255, 194, 94, 0.18), transparent 25%),
    linear-gradient(135deg, #06131a 0%, #101b28 52%, #070b11 100%);
}

.revenue-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px);
  background-size: 52px 52px;
  opacity: 0.42;
}

.revenue-header,
.metric-grid,
.analytics-grid,
.report-card {
  position: relative;
  z-index: 1;
}

.revenue-header {
  display: flex;
  justify-content: space-between;
  gap: 22px;
  align-items: flex-end;
  margin-bottom: 22px;
}

.eyebrow {
  margin: 0 0 9px;
  color: rgba(255, 205, 145, 0.84);
  letter-spacing: 0.28em;
  font-size: 12px;
}

.revenue-header h1 {
  margin: 0;
  font-size: clamp(34px, 5vw, 64px);
  font-family: ui-serif, serif;
}

.revenue-header p,
.metric-card p {
  margin: 10px 0 0;
  color: rgba(255, 247, 236, 0.72);
}

.header-actions,
.filter-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.ghost-btn,
.primary-btn {
  border: none;
  border-radius: 999px;
  cursor: pointer;
  padding: 11px 16px;
}

.ghost-btn {
  color: #fff7ec;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.06);
}

.primary-btn {
  color: #07121d;
  font-weight: 700;
  background: linear-gradient(135deg, #86f1bd, #ffd08a);
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}

.metric-card,
.panel-card,
.report-card {
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(8, 18, 26, 0.78);
  backdrop-filter: blur(18px);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.24);
}

.metric-card {
  padding: 20px;
  border-radius: 22px;
}

.metric-card span {
  color: rgba(255, 205, 145, 0.86);
  font-size: 13px;
}

.metric-card strong {
  display: block;
  margin-top: 12px;
  font-size: 34px;
  line-height: 1;
}

.analytics-grid {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 18px;
  margin-bottom: 18px;
}

.panel-card,
.report-card {
  padding: 22px;
  border-radius: 24px;
}

.panel-title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 18px;
}

.panel-title h2 {
  margin: 0;
  font-size: 24px;
}

.panel-title > span {
  color: #8ff1c7;
}

.trend-list,
.service-list {
  display: grid;
  gap: 12px;
}

.trend-row {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr) 92px;
  align-items: center;
  gap: 12px;
}

.trend-row span,
.service-row span {
  color: rgba(255, 247, 236, 0.64);
  font-size: 13px;
}

.trend-track {
  height: 11px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
}

.trend-track i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #8ff1c7, #ffd08a);
}

.trend-row strong,
.service-row em {
  color: #fff7ec;
  font-style: normal;
  text-align: right;
}

.service-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.055);
}

.service-row strong {
  display: block;
  margin-bottom: 6px;
}

.revenue-table {
  border-radius: 16px;
  overflow: hidden;
}

@media (max-width: 980px) {
  .revenue-header,
  .analytics-grid {
    grid-template-columns: 1fr;
  }

  .revenue-header {
    flex-direction: column;
    align-items: stretch;
  }

  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .revenue-page {
    padding: 18px;
  }

  .metric-grid {
    grid-template-columns: 1fr;
  }
}
</style>
